import React from "react";
import { Navigate, Outlet, ScrollRestoration, createBrowserRouter, RouterProvider } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Home from "./pages/Home";
import LandingDos from "./pages/LandingDos";
import BusinessPage from "./pages/BusinessPage";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Plans from "./pages/Plans";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import DemoPage from "./pages/DemoPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentError from "./pages/PaymentError";
import PaymentDiagnostic from "./pages/PaymentDiagnostic";
import DevOnboarding from "./pages/DevOnboarding";
import DevWhatsApp from "./pages/DevWhatsApp";
import DevFondos from "./pages/DevFondos";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { DemoProvider } from "./contexts/DemoContext";
import { devUtils } from "./config/development";
import { Toaster } from "./components/ui/sonner";

// Initialize development mode
devUtils.enableDevMode();
devUtils.suppressWarnings();

/**
 * Envoltorio de todas las rutas. Existe sólo para colgar <ScrollRestoration/>.
 *
 * En una SPA el documento nunca se recarga, así que el navegador conserva la
 * posición del scroll al cambiar de ruta y React Router no la toca por su
 * cuenta. El síntoma: cualquier <Link> del footer —que está, por definición,
 * al fondo de la página— abría la pantalla siguiente ya scrolleada hasta
 * abajo. El "Ver Demo" del CTA del footer llevaba al final de /demo, con las
 * tarjetas de la demo fuera de la vista.
 *
 * ScrollRestoration cubre los tres casos y en este orden: con atrás/adelante
 * devuelve la posición que tenía esa pantalla, con ancla (/#precios) scrollea
 * al elemento, y en cualquier otra navegación va arriba de todo.
 *
 * Los links del footer que son <a href> y no <Link> (components/ui/footer.jsx)
 * nunca tuvieron el problema, porque recargan el documento entero. Se quedan
 * así: son los que llevan anclas de la landing y el salto al ancla lo resuelve
 * el navegador.
 */
function RaizDelSitio() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

// Create router with future flags to fix deprecation warnings
const router = createBrowserRouter([
  {
    element: <RaizDelSitio />,
    children: [
      /* LA LANDING ES `LandingDos`. Se decidió; la de antes queda abajo en
         /landing-1 para poder compararlas, no como alternativa viva.

         Las tres rutas van acá y no al final porque `/:slug` es el catch-all de
         las páginas de negocio: declaradas después, "landing-1" se leería como
         el slug de un negocio y mostraría "no existe". */
      {
        path: "/",
        element: <LandingDos />
      },
      /* /landing-2 redirige y no vuelve a montar el componente: dos URLs
         sirviendo la misma página compiten entre sí en el buscador, y la que
         tiene los enlaces es "/". Se queda como redirección porque hay links
         dando vueltas —el equipo la anduvo mirando durante semanas— y un 404
         para eso no ayuda a nadie. */
      {
        path: "/landing-2",
        element: <Navigate to="/" replace />
      },
      /* La landing vieja. NO SE BORRA TODAVÍA: sigue siendo con qué comparar, y
         además `Home.css` se carga por su import y varias hojas del panel están
         escritas contra reglas que se le escapan (lo dicen AgregarTurno.css y
         BusinessPage.css en sus comentarios). Borrar la página es una limpieza
         aparte, con esas hojas revisadas. */
      {
        path: "/landing-1",
        element: <Home />
      },
      {
        path: "/auth/login",
        element: <Login />
      },
      {
        path: "/auth/register",
        element: <Register />
      },
      {
        path: "/auth/forgot-password",
        element: <ForgotPassword />
      },
      {
        path: "/legal/privacy",
        element: <PrivacyPolicyPage />
      },
      {
        path: "/legal/terms",
        element: <TermsOfServicePage />
      },
      {
        path: "/admin/onboarding",
        element: (
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        )
      },
      {
        path: "/admin/dashboard",
        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        )
      },
      {
        path: "/admin/plans",
        element: (
          <ProtectedRoute>
            <Plans />
          </ProtectedRoute>
        )
      },
      {
        path: "/payment-success",
        element: (
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        )
      },
      {
        path: "/payment-error",
        element: (
          <ProtectedRoute>
            <PaymentError />
          </ProtectedRoute>
        )
      },
      {
        path: "/payment-diagnostic",
        element: (
          <ProtectedRoute>
            <PaymentDiagnostic />
          </ProtectedRoute>
        )
      },
      {
        path: "/demo",
        element: <DemoPage />
      },
      {
        path: "/demo/business",
        element: <BusinessPage isDemo={true} />
      },
      {
        path: "/demo/dashboard",
        element: <Dashboard isDemo={true} />
      },
      // Bancos de pruebas, solo en desarrollo. Van antes de "/:slug" porque ese es
      // el comodín que se queda con cualquier ruta de primer nivel. En producción
      // el array queda vacío y las rutas no existen.
      ...(process.env.NODE_ENV === 'development'
        ? [
            { path: "/dev/onboarding", element: <DevOnboarding /> },
            { path: "/dev/whatsapp", element: <DevWhatsApp /> },
            // Banco de pruebas de fondos para la landing. Ver DevFondos.jsx.
            { path: "/dev/fondos", element: <DevFondos /> }
          ]
        : []),
      {
        path: "/:slug",
        element: <BusinessPage />
      },
      {
        path: "*",
        element: <Navigate to="/" replace />
      }
    ]
  }
], {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
});

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <DemoProvider>
          <RouterProvider router={router} />
          {/* Los avisos al usuario salían por window.alert(), que bloquea la
              página y no se puede estilar. Este Toaster los recibe. */}
          <Toaster position="top-center" richColors closeButton />
        </DemoProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
